import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { VisibilityProvider } from "@/contexts/VisibilityContext";
import { CartProvider } from "@/contexts/CartContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SiteModeProvider, useSiteMode } from "@/contexts/SiteModeContext";
import ComingSoon from "@/pages/ComingSoon";
import Maintenance from "@/pages/Maintenance";
import NotFound from "@/pages/NotFound";
import ServerError from "@/pages/ServerError";

import PublicLayout from "@/components/layout/PublicLayout";
import DashboardLayout from "@/components/layout/DashboardLayout";

import Home from "@/pages/Home";
import OemOdm from "@/pages/OemOdm";
import HomeAutomation from "@/pages/HomeAutomation";
import IndustrialAutomation from "@/pages/IndustrialAutomation";
import Marketplace from "@/pages/Marketplace";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Platforms from "@/pages/Platforms";
import Opportunities from "@/pages/Opportunities";
import Apply from "@/pages/Apply";
import Investors from "@/pages/Investors";
import Founders from "@/pages/Founders";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VisibilityGuard from "@/components/VisibilityGuard";

// Dashboard + AdminConsole are heavy and only used post-login. Lazy-load
// them so public visitors don't ship the admin bundle. Suspense fallback
// below is intentionally quiet -- a full-page spinner would flash on
// route change and hurt perceived speed.
const Dashboard = lazy(() => import("@/pages/dashboard/Index"));
const AdminConsole = lazy(() => import("@/pages/dashboard/AdminConsole"));

// Top-level /admin/:section convenience redirect — keeps the canonical /dashboard/admin/:section
// working as the single source of truth, but stops anyone hitting /admin/<x> from bouncing to 404.
function AdminAlias() {
  const { section } = useParams();
  return <Navigate to={`/dashboard/admin/${section || "overview"}`} replace />;
}

function SiteModeGate({ children }) {
  const { mode, ready, bypassed } = useSiteMode();
  // Never blank-screen: render children until we know mode is actively gating
  if (!ready && !bypassed) return null;
  const isDashboard = window.location.pathname.startsWith("/dashboard");
  if (ready && !bypassed && !isDashboard && mode === "coming_soon") return <ComingSoon />;
  if (ready && !bypassed && !isDashboard && mode === "maintenance") return <Maintenance />;
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <SiteModeProvider>
    <AuthProvider>
      <VisibilityProvider>
        <CartProvider>
          <BrowserRouter>
            <SiteModeGate>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/oem-odm" element={<OemOdm />} />
                <Route path="/home-automation" element={<HomeAutomation />} />
                <Route path="/industrial-automation" element={<IndustrialAutomation />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/orders/:orderId" element={<OrderConfirmation />} />
                <Route path="/platforms" element={<VisibilityGuard visKey="page.platforms"><Platforms /></VisibilityGuard>} />
                <Route path="/opportunities" element={<Opportunities />} />
                <Route path="/opportunities/:type" element={<Apply />} />
                <Route path="/investors" element={<VisibilityGuard visKey="page.investors"><Investors /></VisibilityGuard>} />
                <Route path="/founders" element={<VisibilityGuard visKey="page.founders"><Founders /></VisibilityGuard>} />
                <Route path="/blog" element={<VisibilityGuard visKey="page.blog"><Blog /></VisibilityGuard>} />
                <Route path="/blog/:slug" element={<VisibilityGuard visKey="page.blog"><BlogPost /></VisibilityGuard>} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
              </Route>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Suspense fallback={null}><Dashboard /></Suspense>} />
                <Route path="/dashboard/admin/:section" element={<Suspense fallback={null}><AdminConsole /></Suspense>} />
                <Route path="/dashboard/admin" element={<Suspense fallback={null}><AdminConsole /></Suspense>} />
              </Route>
              {/* Convenience aliases so direct URLs like /admin/blog or /admin/ai
                  redirect to the canonical /dashboard/admin/<section> route. */}
              <Route path="/admin" element={<Navigate to="/dashboard/admin/overview" replace />} />
              <Route path="/admin/:section" element={<AdminAlias />} />
              <Route path="/500" element={<ServerError />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </SiteModeGate>
            <Toaster richColors position="top-right" theme="dark" />
          </BrowserRouter>
        </CartProvider>
      </VisibilityProvider>
    </AuthProvider>
    </SiteModeProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
